import { useState } from "react";
import { DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Loader2,
  ClipboardList,
  Clock,
  CheckCircle,
  Inbox,
  Calendar,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { dialog } from "../../../components/ui/Dialog";

export default function QuestionnaireForm({
  clientId,
  emailClient,
  clientName,
  questionnaires,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("EN_ATTENTE");
  const { utilisateur } = useEtatGlobal();

  const handleSubmit = async () => {
    if (!title || !description) return;

    setLoading(true);

    try {
      const body = {
        title,
        description,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        clientId,
        emailClient,
        emailCollaborateur: utilisateur.email,
        clientName,
      };

      const response = await fetch(
        `https://backend-myalfa.vercel.app/api/CreateQuestionnaire`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error("Erreur lors de la création du questionnaire");
      }

      const result = await response.json();
      console.log("Questionnaire créé :", result);

      dialog.alert("Questionnaire envoyé avec succès ✅");

      setTitle("");
      setDescription("");
      setDueDate("");
    } catch (error) {
      console.error(error);
      dialog.alert("Erreur lors de l'envoi ❌");
    } finally {
      setLoading(false);
    }
  };

  // — logique onglets —
  const safeQuestionnaires = questionnaires ?? [];
  const filteredQuestionnaires = safeQuestionnaires.filter((q) =>
    activeTab === "all" ? true : q.status === activeTab
  );
  const enAttenteCount = safeQuestionnaires.filter(
    (q) => q.status === "EN_ATTENTE"
  ).length;
  const completedCount = safeQuestionnaires.filter(
    (q) => q.status === "completed"
  ).length;

  return (
    <div className="mt-8">
      {/* ── Formulaire de création ── */}
      <div>
        <DialogTitle>Créer un questionnaire</DialogTitle>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <label>Titre</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Questionnaire fiscal 2026"
            />
          </div>

          <div className="space-y-2">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ajoutez une description..."
              rows={3}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "0.75rem",
                padding: "0.5rem",
                fontSize: "0.875rem",
                width: "100%",
              }}
            />
          </div>

          <div className="space-y-2">
            <label>Date limite</label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={!title || !description || loading}
              className="flex-1 bg-[#840040] hover:bg-[#a00050]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                "Créer le questionnaire"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Séparateur ── */}
      <div className="border-t border-slate-100 mt-8 pt-6">
        <h3 className="text-base font-semibold text-slate-800 mb-4">
          Questionnaires envoyés
        </h3>

        {/* Onglets */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100 p-1 mb-4">
            <TabsTrigger
              value="all"
              className="data-[state=active]:bg-white gap-2 text-xs"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              Tous ({safeQuestionnaires.length})
            </TabsTrigger>
            <TabsTrigger
              value="EN_ATTENTE"
              className="data-[state=active]:bg-white gap-2 text-xs"
            >
              <Clock className="w-3.5 h-3.5" />À compléter ({enAttenteCount})
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="data-[state=active]:bg-white gap-2 text-xs"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Complétés ({completedCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Liste */}
        {filteredQuestionnaires.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Inbox className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700">
              Aucun questionnaire
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {activeTab === "EN_ATTENTE"
                ? "Le client est à jour !"
                : "Aucun questionnaire disponible"}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filteredQuestionnaires.map((q) => (
              <div
                key={q.id}
                className="bg-slate-50 border border-slate-200 rounded-xl p-4"
              >
                {/* En-tête de la carte */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-sm font-semibold text-slate-800 leading-snug">
                    {q.title}
                  </p>
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                      q.status === "EN_ATTENTE"
                        ? "bg-blue-50 text-blue-600"
                        : "bg-emerald-50 text-emerald-600"
                    }`}
                  >
                    {q.status === "EN_ATTENTE" ? "En attente" : "Complété"}
                  </span>
                </div>

                {/* Description */}
                <p className="text-xs text-slate-500 leading-relaxed mb-3">
                  {q.description}
                </p>

                {/* Date */}
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
                  <Calendar className="w-3 h-3 opacity-60" />
                  {q.status === "EN_ATTENTE"
                    ? `À compléter avant le ${new Date(
                        q.dueDate
                      ).toLocaleDateString("fr-FR")}`
                    : `✓ Complété le ${new Date(
                        q.completedAt
                      ).toLocaleDateString("fr-FR")}`}
                </div>

                {/* Réponse du client (uniquement si complété et answer non vide) */}
                {q.status !== "EN_ATTENTE" && q.answer && (
                  <div className="bg-white border border-emerald-100 rounded-lg px-3 py-2.5">
                    <p className="text-xs font-semibold text-emerald-700 mb-1">
                      Réponse du client
                    </p>
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {q.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
